/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import { createCustomElement, DOMModel, byAttrVal, byJsonAttrVal, byBooleanAttrVal } from '@adobe/react-webcomponent';
import { Provider, defaultTheme } from '@adobe/react-spectrum';

import withRoot from '../components/Root/withRoot';
import useLocale from '../components/common/useLocale';
import PickerModal from '../components/PickerModal/PickerModal';

const ContextWrapper = props => {
    const locale = useLocale();
    const domRoot = useRef(null);

    return (
        <div ref={domRoot}>
            <Provider theme={defaultTheme} colorScheme="light" locale={locale}>
                <PickerModal {...props} domRoot={domRoot} />
            </Provider>
        </div>
    );
};

ContextWrapper.propTypes = {
    show: PropTypes.bool,
    type: PropTypes.oneOf(['product', 'category']).isRequired
};

class PickerModel extends DOMModel {
    @byJsonAttrVal() configs;
    @byAttrVal('selection-id') selectionId;
    @byBooleanAttrVal() multiple;
    @byBooleanAttrVal() show;
    @byAttrVal() type;
    @byAttrVal() target;
    @byBooleanAttrVal('enable-preview-date-filter') enablePreviewDateFilter;
}

(function () {
    const PickerElement = createCustomElement(withRoot(ContextWrapper), PickerModel, 'element');

    const elemName = 'cif-picker';
    if (!window.customElements.get(elemName)) {
        window.customElements.define(elemName, PickerElement);
    }
})();
